import * as React from "react";
import { AlertCircle, Loader2 } from "lucide-react";
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Textarea,
} from "@eduviaos/ui-kit";
import {
  ApiError,
  SLUG_RULES,
  boundedNameSchema,
  deriveSlug,
  fieldError,
  slugProblemFor,
  type AssessmentTopicCreate,
  type AssessmentTopicPublic,
  type AssessmentTopicUpdate,
} from "@eduviaos/shared";
import {
  useAssessmentTopics,
  useCreateAssessmentTopic,
  useUpdateAssessmentTopic,
} from "@/hooks/useAssessments";

/**
 * Phase H5 FE-1 — Topic editor sheet.
 *
 * Mode-switching create / edit sheet for `QuestionTopic` rows
 * (`/assessments/topics`). Grade band is captured as two number inputs
 * with an "applies to every grade" escape hatch (BE accepts `null`).
 *
 * The slug is derived from the name on create and never shown. It is
 * fixed after creation because blueprint items reference it, so a
 * rename only changes the display name.
 */

const TOPIC_NAME_MAX = 120;
const SUBJECT_MAX = 80;
const topicNameSchema = boundedNameSchema(TOPIC_NAME_MAX, "Topic name");
const subjectSchema = boundedNameSchema(SUBJECT_MAX, "Subject");

interface DraftState {
  name: string;
  subject: string;
  description: string;
  parentSlug: string | null;
  gradeMin: number | null;
  gradeMax: number | null;
}

const EMPTY_DRAFT: DraftState = {
  name: "",
  subject: "",
  description: "",
  parentSlug: null,
  gradeMin: null,
  gradeMax: null,
};

function existingToDraft(t: AssessmentTopicPublic): DraftState {
  return {
    name: t.name,
    subject: t.subject,
    description: t.description ?? "",
    parentSlug: t.parent_slug,
    gradeMin: t.grade_band?.min ?? null,
    gradeMax: t.grade_band?.max ?? null,
  };
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existing: AssessmentTopicPublic | null;
  onSaved: (saved: AssessmentTopicPublic) => void;
}

export function AssessmentTopicEditorSheet({ open, onOpenChange, existing, onSaved }: Props) {
  const mode: "create" | "edit" = existing ? "edit" : "create";
  const [draft, setDraft] = React.useState<DraftState>(EMPTY_DRAFT);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [showFieldErrors, setShowFieldErrors] = React.useState(false);
  const [serverNameError, setServerNameError] = React.useState<string | null>(null);

  const createMutation = useCreateAssessmentTopic();
  const updateMutation = useUpdateAssessmentTopic();
  const topicsQuery = useAssessmentTopics({ enabled: open });

  const isPending = createMutation.isPending || updateMutation.isPending;

  React.useEffect(() => {
    if (!open) return;
    setErrorMessage(null);
    setShowFieldErrors(false);
    setServerNameError(null);
    setDraft(existing ? existingToDraft(existing) : EMPTY_DRAFT);
  }, [open, existing]);

  const handleNameChange = (next: string) => {
    setServerNameError(null);
    setDraft((prev) => ({ ...prev, name: next }));
  };

  const parents = React.useMemo(
    () => (topicsQuery.data?.items ?? []).filter((t) => !existing || t.slug !== existing.slug),
    [topicsQuery.data?.items, existing],
  );

  const derivedSlug = deriveSlug(draft.name, SLUG_RULES.assessment);
  const nameError =
    serverNameError ??
    fieldError(topicNameSchema, draft.name) ??
    (mode === "create"
      ? slugProblemFor(derivedSlug, SLUG_RULES.assessment, {
          noun: "topic",
          taken: (topicsQuery.data?.items ?? []).map((t) => t.slug),
        })
      : null);
  const subjectError = fieldError(subjectSchema, draft.subject);
  const visibleNameError = showFieldErrors || serverNameError ? nameError : null;
  const visibleSubjectError = showFieldErrors ? subjectError : null;

  const validate = (): string | null => {
    if (draft.gradeMin !== null && draft.gradeMax !== null) {
      if (draft.gradeMin < 1 || draft.gradeMin > 12) return "Grade-min must be between 1 and 12.";
      if (draft.gradeMax < 1 || draft.gradeMax > 12) return "Grade-max must be between 1 and 12.";
      if (draft.gradeMin > draft.gradeMax) {
        return "Grade-min must be ≤ grade-max.";
      }
    } else if ((draft.gradeMin === null) !== (draft.gradeMax === null)) {
      return "Set both grade-min and grade-max, or leave both blank for any grade.";
    }
    return null;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setErrorMessage(null);
    setShowFieldErrors(true);
    if (nameError || subjectError) return;
    const validation = validate();
    if (validation) {
      setErrorMessage(validation);
      return;
    }
    const grade_band =
      draft.gradeMin !== null && draft.gradeMax !== null
        ? { min: draft.gradeMin, max: draft.gradeMax }
        : null;
    try {
      if (mode === "create") {
        const body: AssessmentTopicCreate = {
          name: draft.name.trim(),
          slug: derivedSlug,
          subject: draft.subject.trim(),
          description: draft.description.trim() || null,
          parent_slug: draft.parentSlug,
          grade_band,
        };
        const saved = await createMutation.mutateAsync(body);
        onSaved(saved);
        onOpenChange(false);
      } else if (existing) {
        const body: AssessmentTopicUpdate = {
          name: draft.name.trim(),
          subject: draft.subject.trim(),
          description: draft.description.trim() || null,
          parent_slug: draft.parentSlug,
          grade_band,
        };
        const saved = await updateMutation.mutateAsync({ slug: existing.slug, body });
        onSaved(saved);
        onOpenChange(false);
      }
    } catch (raw) {
      if (raw instanceof ApiError && raw.status === 409 && mode === "create") {
        setServerNameError("Another topic already has this name. Choose a different name.");
      } else if (raw instanceof ApiError && raw.message) {
        setErrorMessage(raw.message);
      } else if (raw instanceof Error && raw.message) {
        setErrorMessage(raw.message);
      } else {
        setErrorMessage("Could not save — please try again.");
      }
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full max-w-lg flex-col gap-4 overflow-y-auto sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>{mode === "create" ? "New topic" : `Edit · ${existing?.name}`}</SheetTitle>
          <SheetDescription>
            Topics scope every question and every blueprint item. Renaming a topic keeps every
            template that uses it working.
          </SheetDescription>
        </SheetHeader>

        <form className="grid gap-4 pt-2" noValidate onSubmit={(e) => void handleSubmit(e)}>
          <div className="grid gap-1.5">
            <Label htmlFor="topic-name">Name</Label>
            <Input
              id="topic-name"
              value={draft.name}
              onChange={(e) => handleNameChange(e.target.value)}
              onBlur={() => setShowFieldErrors(true)}
              placeholder="Algebra basics"
              maxLength={TOPIC_NAME_MAX}
              aria-invalid={visibleNameError ? true : undefined}
              aria-describedby={visibleNameError ? "topic-name-error" : undefined}
              autoFocus
            />
            {visibleNameError ? (
              <p id="topic-name-error" className="text-destructive text-xs">
                {visibleNameError}
              </p>
            ) : null}
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="topic-subject">Subject</Label>
            <Input
              id="topic-subject"
              value={draft.subject}
              onChange={(e) => setDraft((p) => ({ ...p, subject: e.target.value }))}
              placeholder="Mathematics"
              maxLength={SUBJECT_MAX}
              aria-invalid={visibleSubjectError ? true : undefined}
              aria-describedby={visibleSubjectError ? "topic-subject-error" : undefined}
            />
            {visibleSubjectError ? (
              <p id="topic-subject-error" className="text-destructive text-xs">
                {visibleSubjectError}
              </p>
            ) : null}
            <span className="text-muted-foreground text-[11px]">
              Free-text bucket — typically the school subject this topic rolls up under.
            </span>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="topic-description">Description (optional)</Label>
            <Textarea
              id="topic-description"
              value={draft.description}
              onChange={(e) => setDraft((p) => ({ ...p, description: e.target.value }))}
              placeholder="One-liner that helps authors pick the right tag."
              maxLength={1000}
              rows={3}
            />
          </div>

          <div className="grid gap-1.5">
            <Label>Parent topic (optional)</Label>
            <Select
              value={draft.parentSlug ?? "__none__"}
              onValueChange={(v) =>
                setDraft((p) => ({ ...p, parentSlug: v === "__none__" ? null : v }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="No parent" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">No parent — top-level</SelectItem>
                {parents.map((p) => (
                  <SelectItem key={p.slug} value={p.slug}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-muted-foreground text-[11px]">
              Nesting is shallow by design — max depth is enforced by the BE to keep the picker
              navigable.
            </span>
          </div>

          <fieldset className="grid gap-1.5 rounded-md border p-3">
            <legend className="text-foreground px-1 text-xs font-medium">Grade band</legend>
            <span className="text-muted-foreground text-[11px]">
              Optional. Set both fields to scope the topic to a grade range (e.g. 6–8); leave both
              blank to apply to every grade.
            </span>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <div className="grid gap-1">
                <Label htmlFor="topic-grade-min" className="text-xs">
                  Grade min
                </Label>
                <Input
                  id="topic-grade-min"
                  type="number"
                  min={1}
                  max={12}
                  value={draft.gradeMin ?? ""}
                  onChange={(e) =>
                    setDraft((p) => ({
                      ...p,
                      gradeMin: e.target.value === "" ? null : Number(e.target.value),
                    }))
                  }
                  placeholder="—"
                />
              </div>
              <div className="grid gap-1">
                <Label htmlFor="topic-grade-max" className="text-xs">
                  Grade max
                </Label>
                <Input
                  id="topic-grade-max"
                  type="number"
                  min={1}
                  max={12}
                  value={draft.gradeMax ?? ""}
                  onChange={(e) =>
                    setDraft((p) => ({
                      ...p,
                      gradeMax: e.target.value === "" ? null : Number(e.target.value),
                    }))
                  }
                  placeholder="—"
                />
              </div>
            </div>
          </fieldset>

          {errorMessage ? (
            <div
              role="alert"
              className="border-destructive/40 bg-destructive/5 text-destructive flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
            >
              <AlertCircle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
              <span className="flex-1">{errorMessage}</span>
            </div>
          ) : null}

          <div className="mt-2 flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? (
                <>
                  <Loader2 aria-hidden="true" className="size-4 animate-spin" />
                  Saving…
                </>
              ) : mode === "create" ? (
                "Create topic"
              ) : (
                "Save changes"
              )}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
